import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { workingSchedulesApi } from './api';
import type { CreateWorkingScheduleInput, UpdateWorkingScheduleInput } from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useWorkingSchedules() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['working-schedules', tenantId, 'list'],
    queryFn: () => workingSchedulesApi.list(),
    staleTime: 30_000,
  });
}

export function useWorkingSchedule(id: string | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['working-schedules', tenantId, 'detail', id],
    queryFn: () => workingSchedulesApi.get(id as string),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** Every write refreshes the list and the open detail (counts, holidays, assignments). */
function useScheduleMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['working-schedules', tenantId] }); },
  });
}

export function useCreateWorkingSchedule() {
  return useScheduleMutation((input: CreateWorkingScheduleInput) => workingSchedulesApi.create(input));
}

export function useUpdateWorkingSchedule() {
  return useScheduleMutation(({ id, input }: { id: string; input: UpdateWorkingScheduleInput }) =>
    workingSchedulesApi.update(id, input));
}

export function useDeleteWorkingSchedule() {
  return useScheduleMutation((id: string) => workingSchedulesApi.remove(id));
}

export function useAddHoliday() {
  return useScheduleMutation(({ id, holiday_date, name }: { id: string; holiday_date: string; name: string }) =>
    workingSchedulesApi.addHoliday(id, { holiday_date, name }));
}

export function useRemoveHoliday() {
  return useScheduleMutation(({ id, holidayId }: { id: string; holidayId: string }) =>
    workingSchedulesApi.removeHoliday(id, holidayId));
}

export function useAssignLocationSchedule() {
  return useScheduleMutation(({ locationId, scheduleId }: { locationId: string; scheduleId: string | null }) =>
    workingSchedulesApi.assignLocation(locationId, scheduleId));
}

export function useAssignUserSchedule() {
  return useScheduleMutation(({ userId, scheduleId }: { userId: string; scheduleId: string | null }) =>
    workingSchedulesApi.assignUser(userId, scheduleId));
}
